import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Drawer, InputRef, message, Modal, Space, Table, Typography } from "antd";
import { EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { SorterResult } from "antd/es/table/interface";
import { ColumnsType } from "antd/es/table";
import moment from "moment";

import { SCMaxDLogsService } from 'services'
import { ScmaxDlogRecord } from "types";
import CustomBreadcrumb from "components/CustomBreadcrumb";
import { GetSCMaxDLogsRecordsParams } from "services/scmax-dlogs";
import SCMaxDLogsFilters from "../components/SCMaxDLogRecordsFilters/"
import { SCMaxDLogsFilter } from "../types";
import { objectToUrlParams, urlParamsToObject } from "../../../utils/url-utils";
import { history } from "../../../utils/history";
import { deepCamelCaseKeys } from "../../../utils/objects";
import { getColumnSearchProps } from "../../../components/TableSearch";
import SCMaxZapTableRecord from '../components/SCMaxRecordZap';

// ----- Global variables -----
const DEFAULT_PAGE_SIZE = 100


const ScmaxDLogsListPage = () => {
	// ----- Navigation -----
	const [searchParams] = useSearchParams();

	// ----- Data -----
	const [SCMaxDLogs, setDiagLogs] = useState<ScmaxDlogRecord[]>([])
	const [selectedSCMaxDLogId, setSelectedSCMaxDLogId] = useState<number | null>(null)
	const [loading, setLoading] = useState<boolean>(false)

	// ----- Filters -----
	const [filters, setFilters] = useState<SCMaxDLogsFilter>()

	// ----- Modal -----
	const [isModalOpen, setIsModalOpen] = useState(false);

	// ----- Table -----
	const [totalItems, setTotalItems] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)// todo: add search field

	const searchInput = useRef<InputRef>(null);
	const [searchText, setSearchText] = useState('');
	const [searchedColumn, setSearchedColumn] = useState('');
	const [search,] = useState<string>('')
	const [sortField, setSortField] = useState<string | undefined>()
	const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | undefined>()

	// ----- Drawer -----
	// todo: if mac id present in the url no drawer is needed
	const [open, setOpen] = useState(true);

	// ----- Hooks -----
	useEffect(() => {
		// fetch data from server
		fetchData()
	}, [currentPage, search, sortField, sortOrder, filters])

	useEffect(() => {
		// get filters from url
		console.log(searchParams)
		const filtersInUrl = urlParamsToObject<SCMaxDLogsFilter>(searchParams)
		console.log("filters from url")
		console.log(filtersInUrl)
		//const types = Array.isArray(filtersInUrl?.rids) ? filtersInUrl?.rids : Array([filtersInUrl?.rids])
		const rids = Array.isArray(filtersInUrl?.rids) ? [filtersInUrl?.rids] : (filtersInUrl?.rids ? [filtersInUrl?.rids] : [])
		if (filtersInUrl) {
			// Update the filters state
			setFilters(prevFilters => ({
				...prevFilters,
				...filtersInUrl,
				rids,
			}));
		}
		// Run this effect whenever 'searchParams' changes
	}, [searchParams])

	// ----- Drawer -----
	const showDrawer = () => {
		setOpen(true);
	};

	const onClose = () => {
		setOpen(false);
	};
	// ----- End Drawer -----

	// ----- Modal -----
	const showModal = () => {
		setIsModalOpen(true);
	};

	const handleOk = () => {
		setIsModalOpen(false);
		setSelectedSCMaxDLogId(null);
	};

	const handleCancel = () => {
		setIsModalOpen(false);
		setSelectedSCMaxDLogId(null);
	};
	// ----- End Modal -----

	const fetchData = async () => {
		if (!filters?.macAddress) return
		console.log("fetch data")
		console.log(filters)
		setLoading(true)
		try {
			const params: GetSCMaxDLogsRecordsParams = {
				mac_address: filters?.macAddress || '', // "FDC6E5B9CFD1",
				rids: filters?.rids || [],
				...(filters?.startDate ? { start_date: filters?.startDate } : {}),
				...(filters?.endDate ? { end_date: filters?.endDate } : {}),
				page: currentPage,
				page_size: pageSize,
			}
			const response = await SCMaxDLogsService.getSCMaxDLogsRecords(params)
			if (!response)
				throw Error("Invalid response")
			const { items, count } = response

			const formattedSCMaxDLogs = items?.map((item: ScmaxDlogRecord) => ({
				...deepCamelCaseKeys<ScmaxDlogRecord>(item),
				key: `scmax-dlog-record-${item?.id}`,
			}))
			setDiagLogs(formattedSCMaxDLogs)
			setTotalItems(count)
		} catch (err) {
			// Show error message
			console.error(err)
			console.error('Error getting diag logs')
			message.error(`Failed to get diag-logs`);
		}
		setLoading(false)
	}

	// ----- Handlers -----
	const handleViewRecordDetails = (record: ScmaxDlogRecord) => {
		setSelectedSCMaxDLogId(record.id)
		showModal()
	}

	const handleOnFilter = (values: SCMaxDLogsFilter) => {
		setFilters(values)
		setOpen(false)
		// add filters to url
		const url = objectToUrlParams(values);
		history?.navigate?.(url);
	}

	const columns: ColumnsType<ScmaxDlogRecord> = [
		{
			title: 'Id',
			dataIndex: 'id',
			key: 'id',
			...getColumnSearchProps({
				searchInput,
				setSearchText,
				setSearchedColumn,
				searchedColumn,
				searchText,
				dataIndex: 'id'
			}),
		},
		{
			title: 'Type',
			dataIndex: 'name',
			key: 'name',
			render: (value) => value?.replace('Record', '')
		},
		{
			title: 'Timestamp',
			dataIndex: 'ts',
			key: 'ts',
			render: (value) => <>{value}</>
		},
		{
			title: 'Info',
			dataIndex: 'parsedJson',
			key: 'parsedJson',
			render: (value: object, record: ScmaxDlogRecord) => {
				switch (record?.rid) {
					case 32:
						return <SCMaxZapTableRecord {...value} />
					default:
						return <>{JSON.stringify(value)}</>
				}
			}
		}, {
			title: 'Action',
			dataIndex: 'action',
			key: 'action',
			render: (_: object, record: ScmaxDlogRecord) => {
				return (
					<Button onClick={() => handleViewRecordDetails(record)} type="primary" icon={<EyeOutlined />} />
				)
			}

		}
	]

	const selectedSCMaxDiagLog = SCMaxDLogs.find(item => item.id === selectedSCMaxDLogId) || null
	return (
		<>
			<CustomBreadcrumb list={['SCMAX DLogs', 'List',]} />
			<Card title="SCMAX Diag logs"
				extra={
					<Space>
						<Button
							loading={loading}
							onClick={() => fetchData()}
							icon={<ReloadOutlined />} />
						<Button type="primary"
							loading={loading}
							onClick={() => showDrawer()}
						>
							Filters
						</Button>
					</Space>
				}
			>
				<Table<ScmaxDlogRecord>
					columns={columns}
					dataSource={SCMaxDLogs}
					loading={loading}
					size="small"
					bordered
					pagination={{
						pageSize: pageSize,
						current: currentPage,
						total: totalItems,
						showSizeChanger: true,
						pageSizeOptions: ['10', '20', '30', '50', '100'],
						showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`
					}}
					onChange={(pagination, filters, sorter: SorterResult<ScmaxDlogRecord> | SorterResult<ScmaxDlogRecord>[],) => {
						// setFilters(filters)
						setPageSize(pagination.pageSize || DEFAULT_PAGE_SIZE);
						setCurrentPage(pagination.current || 1);
						console.log('pagination.current ', pagination.current);
						if (typeof sorter === 'object') {
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							setSortField(sorter?.field?.toString())
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							setSortOrder(sorter?.order?.toString())
						}
					}}
				/>
			</Card>
			<Drawer
				title="Filter"
				placement="left"
				closable={false}
				onClose={onClose}
				open={open}
				key={"DrawerFilter"}
			>
				<SCMaxDLogsFilters onSubmit={handleOnFilter} filters={filters} loading={loading} />
			</Drawer>
			<Modal title="JSON record data" open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
				<pre>{selectedSCMaxDiagLog && JSON.stringify(selectedSCMaxDiagLog, null, 2)}</pre>
			</Modal>
		</>
	)
}

export default ScmaxDLogsListPage
